import React, { useEffect, useState } from 'react';
import { billingService } from '../../api/services/billing.service';
import { CreditAccountDTO } from '../../types/billing';

interface Props {
  value: string;
  onChange: (organizationId: string) => void;
  /**
   * The blank option's text. Reports that are per organisation have no "all": they pass
   * e.g. "Choose an organization", which is then not selectable once one is chosen.
   */
  allLabel?: string;
  /** True: the blank option is a prompt, not a choice. */
  required?: boolean;
}

/**
 * Organisation filter for the platform operator. The account list is platform-wide
 * only (403 for a tenant), and a tenant's lists are its own already, so for them this
 * renders nothing.
 */
const OrgFilterSelect: React.FC<Props> = ({ value, onChange, allLabel = 'All organizations', required = false }) => {
  const [accounts, setAccounts] = useState<CreditAccountDTO[]>([]);

  useEffect(() => {
    let live = true;
    billingService.getCreditAccounts({ page: 1, size: 200 }).then((r) => {
      if (live && r.success && r.data) {
        setAccounts([...r.data.content].sort((a, b) =>
          (a.organizationName || a.organizationId).localeCompare(b.organizationName || b.organizationId)));
      }
    });
    return () => {
      live = false;
    };
  }, []);

  if (accounts.length < 2 && !value) return null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Organization</label>
      <select
        aria-label="Organization"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:ring-indigo-500 focus:border-indigo-500"
      >
        <option value="" disabled={required}>{allLabel}</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.organizationId}>{a.organizationName || a.organizationId}</option>
        ))}
        {value && !accounts.some((a) => a.organizationId === value) && <option value={value}>{value}</option>}
      </select>
    </div>
  );
};

export default OrgFilterSelect;
